import axios from 'axios'

// Get the API base URL - in development it's the same origin, in production it's the deployed URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://www.transcripter.uk' 
  : window.location.origin

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error)
  }
)

export default api

// API endpoints
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    options: { hasAcceptedPrivacyPolicy: boolean; marketingEmailsOptIn?: boolean }
  ) =>
    api.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
      hasAcceptedPrivacyPolicy: options.hasAcceptedPrivacyPolicy,
      marketingEmailsOptIn: options.marketingEmailsOptIn,
    }),

  profile: () => api.get('/auth/profile'),

  updateConsent: (payload: { hasAcceptedPrivacyPolicy?: boolean; marketingEmailsOptIn?: boolean }) =>
    api.put('/auth/privacy', payload),

  logout: () => api.delete('/auth/login'),
}

export const youtubeAPI = {
  getMetadata: (url: string) => 
    api.get('/youtube', { params: { url } }),
}

export const transcriptAPI = {
  getBulkStatus: (bulkJobId: string) =>
    api.get('/transcript/batch', { params: { jobId: bulkJobId } }),
}

export const ragAPI = {
  index: (bulkJobId: string, options?: { force?: boolean }) =>
    api.post('/rag', { action: 'index', bulkJobId, ...(options || {}) }),

  query: (bulkJobId: string, messages: { role: 'user' | 'assistant'; content: string }[], jobIds?: string[], model?: string) =>
    api.post('/rag', { action: 'query', bulkJobId, messages, jobIds, model }),

  session: (bulkJobId: string) =>
    api.get('/rag', { params: { bulkJobId } }),

  models: () =>
    api.get('/rag', { params: { models: 'true' } }),
}

export const dashboardAPI = {
  overview: () => api.get('/transcript/batch', { params: { view: 'dashboard' } }),
}

export const historyAPI = {
  getFolders: () => api.get('/transcript/batch', { params: { view: 'history' } }),
  downloadBatch: (batchId: string) => api.get('/transcript/batch', { 
    params: { jobId: batchId, download: 'zip' }, 
    responseType: 'blob',
    timeout: 120000 // 2 minutes timeout for large downloads
  }),
}

export const billingAPI = {
  summary: () => api.get('/billing', { params: { mode: 'summary' } }),
  checkout: (payload: { plan: string; successUrl?: string; cancelUrl?: string }) =>
    api.post('/billing?mode=checkout', payload),
  portal: (payload?: { returnUrl?: string }) => api.post('/billing?mode=portal', payload ?? {}),
  grant: (payload: { targetUserId?: string; targetEmail?: string; amount: number; reason?: string }) =>
    api.post('/billing?mode=grant', payload),
}

export const gdprAPI = {
  export: () => api.get('/auth/privacy'),
  deleteAccount: () => api.delete('/auth/privacy'),
}
